import React, { useState, useMemo } from 'react'

import { Box, Checkbox, Stack, Typography } from '@mui/material'

import { Modal, Button } from '@repo/ui'
import { UserBlock, Search } from 'components'

import { getRoleDisplayName } from 'constants/roles'
import { useCompanyUsers, useAddRoleMembers } from 'hooks/api'

// ── Props ──────────────────────────────────────────────────────────────────────

interface AssignUsersModalRole {
  id: string
  name: string
  isCustom?: boolean
  builtinRoleId?: string
}

interface AssignUsersModalProps {
  role: AssignUsersModalRole | null
  open: boolean
  onClose: () => void
  onAssigned?: () => void
  /**
   * Ids of users already in this role — excluded from the assignable list.
   * When omitted the component falls back to filtering companyUsers by role name
   * (built-in only; custom roles have no role.name on the user object).
   */
  existingMemberIds?: string[]
}

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * Given a role, pick multiple existing company users to assign to it.
 * Works for both built-in and custom roles via the unified
 * POST /roles/company/:roleId/members endpoint.
 *
 * For built-in roles, assigning replaces the user's current role.
 * For custom roles, the membership is additive (separate join table).
 */
export const AssignUsersModal = ({
  role,
  open,
  onClose,
  onAssigned,
  existingMemberIds,
}: AssignUsersModalProps) => {
  const { data: companyUsersData, isLoading: isLoadingUsers } = useCompanyUsers()

  // The membership roleId the backend needs — built-in roles use builtinRoleId,
  // standalone custom roles use their own id.
  const memberRoleId = role ? (role.builtinRoleId ?? role.id) : ''

  const addMembers = useAddRoleMembers(memberRoleId)

  const [search, setSearch] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  // Reset state whenever modal opens/closes
  React.useEffect(() => {
    if (!open) {
      setSearch('')
      setSelectedIds(new Set())
    }
  }, [open])

  // Users not already in this role
  const assignableUsers = useMemo(() => {
    if (!role) return []
    const all = companyUsersData?.data ?? []

    if (existingMemberIds) {
      // Caller provided the authoritative member id list — use it
      const memberSet = new Set(existingMemberIds)
      return all.filter((u) => !memberSet.has(u.id))
    }

    // Fallback: filter by role name (works for built-in roles where the user
    // object carries role.name; best-effort for custom roles)
    return all.filter((u) => u.role?.name !== role.name)
  }, [companyUsersData, role, existingMemberIds])

  // Client-side filter by search string
  const filteredUsers = useMemo(() => {
    if (!search.trim()) return assignableUsers
    const q = search.toLowerCase()
    return assignableUsers.filter((u) => {
      const name = [u.first_name, u.last_name].filter(Boolean).join(' ').toLowerCase()
      const email = (u.email ?? '').toLowerCase()
      return name.includes(q) || email.includes(q)
    })
  }, [assignableUsers, search])

  const toggleUser = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const handleAssign = async () => {
    if (!role || selectedIds.size === 0 || addMembers.isPending) return
    await addMembers.mutateAsync({ userIds: Array.from(selectedIds) })
    onClose()
    onAssigned?.()
  }

  const roleDisplayName = role ? getRoleDisplayName(role) : ''
  const isCustom = role?.isCustom ?? false

  return (
    <Modal
      title={`Assign users to ${roleDisplayName}`}
      open={open}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={addMembers.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleAssign}
            disabled={selectedIds.size === 0 || addMembers.isPending}
            loading={addMembers.isPending}
          >
            Assign
          </Button>
        </>
      }
    >
      <Stack spacing={3}>
        {/* Note: behaviour differs by role type */}
        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {isCustom
            ? `Assigning adds the selected users to ${roleDisplayName}. Custom roles are additive alongside a user's primary role.`
            : `Assigning replaces each selected user's current role with ${roleDisplayName}. Default roles are exclusive.`}
        </Typography>

        {/* Search box */}
        <Search
          value={search}
          onChange={setSearch}
          onClear={() => setSearch('')}
          placeholder="Search users"
          fullWidth
          debounce={false}
        />

        {/* Scrollable user list */}
        <Box
          sx={(theme) => ({
            maxHeight: 360,
            overflowY: 'auto',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
          })}
        >
          {isLoadingUsers ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
              <Typography variant="body3" color="text.secondary">
                Loading users...
              </Typography>
            </Stack>
          ) : filteredUsers.length === 0 ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
              <Typography variant="body3" color="text.secondary">
                {assignableUsers.length === 0
                  ? 'All users already have this role.'
                  : 'No users match your search.'}
              </Typography>
            </Stack>
          ) : (
            filteredUsers.map((user, index) => (
              <Stack
                key={user.id}
                direction="row"
                alignItems="center"
                spacing={2}
                onClick={() => toggleUser(user.id)}
                sx={(theme) => ({
                  px: 3,
                  py: 2,
                  cursor: 'pointer',
                  borderBottom:
                    index < filteredUsers.length - 1
                      ? `1px solid ${theme.palette.divider}`
                      : 'none',
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                })}
              >
                <Checkbox
                  checked={selectedIds.has(user.id)}
                  onChange={() => toggleUser(user.id)}
                  onClick={(e) => e.stopPropagation()}
                  size="small"
                />
                <UserBlock user={user} />
              </Stack>
            ))
          )}
        </Box>
      </Stack>
    </Modal>
  )
}
